/**
 * ColorPicker is a compound widget with different ways to change the RGB color values.
 *
 * @module mw-colorpicker/ColorPicker
 * @mixes  dijit/_WidgetBase
 * @mixes  dijit/_TemplatedMixin
 * @mixes  dijit/_WidgetsInTemplateMixin
 *
 * @example <caption>MATLAB Color Picker</caption>
 * var colorPicker = new ColorPicker();
 * colorPicker.on("change", function (cpEvent) {
 *     if (e.mwEventData === undefined) {return;}
 *     console.log(cpEvent.mwEventData.newValue);
 * });
 * colorPicker.placeAt(<some dom node>);
 * colorPicker.startup();
 *
 * @copyright Copyright 2017 MathWorks, Inc.
 */
define([
    'dojo/_base/declare',
    'dojo/on',
    'dijit/_WidgetBase',
    'dijit/_TemplatedMixin',
    'dijit/_WidgetsInTemplateMixin',
    'mw-form/ToggleButton',
    './ColorPalette',
    './ColorSpinner',
    './MColor',
    './ColorGradient',
    './ColorStyle',
    './ColorStyleEnum',
    'dojo/text!./templates/ColorPicker.html',
    'dojo/i18n!./l10n/nls/ColorPickerResources'
], function (declare, on, _WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin, ToggleButton,
    ColorPalette, ColorSpinner, MColor, ColorGradient, ColorStyle, ColorStyleEnum, template, l10nStrs) {
    return declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {

        /**
       * @event change
       * @description Event fired on value (color) changes
       * @type {object}
       * @property {object} mwEventData - Object with MW colorpicker event data.
       * @property {string} mwEventData.newValue - ColorPicker value when event was fired.
       * @property {string} mwEventData.oldValue - ColorPicker value prior to event firing.
       * @property {string} mwEventData.propertyName - Name of the property for which
       *                                               this change event was fired.
       */

        /**
       * @event preview
       * @description Event fired as mouse is hovered over Standard Color Cells, Recent Color Cells,
       * Color Style Cells and also when mouse is being dragged along the color gradient
       * @type {object}
       * @property {object} mwEventData - Object with MW colorpicker event data.
       * @property {string} mwEventData.newValue - ColorPicker value when event was fired.
       * @property {string} mwEventData.oldValue - ColorPicker value prior to event firing.
       * @property {string} mwEventData.propertyName - Name of the property for which
       *                                               this change event was fired.
       */

        /**
       * @property {string} value - Hex value of the current color value or name of current
       * color style selected. These values are "auto", "none", "flat", "interp", "texturemap".
       *
       * @instance
       */
        value: '#ff0000',

        /**
       * @property {string[]} recentlyUsedColors - hex color strings for the recent colors.
       * If more than 7 color strings are provided, only the first 7 will be shown.
       * This property must be set and updated by the end-user as needed.
       * There is no data persistence in the Widget itself.
       * When set to an empty array, the RecentColors palette will not be shown.
       * @instance
       */
        recentlyUsedColors: [],

        /**
       * @property {string[]} standardColors - hex color array or array strings for the colors to
       * be used in the Palette. 7 colors per row.
       * Default colors are provided by
       * getStandardColors in {@link module:mw-colorpicker/MColor} ;
       * This property must be set as a construction parameter.
       *
       * @instance
       */
        standardColors: [],

        /**
       * @property {string} displayView - toggles which view is displayed.
       * Valid values are "standard" or "custom"
       * Default value for displayView is "standard".
       *
       * @instance
       */
        displayView: 'standard',

        /**
       * @property {string} autoColor - Hex value of the "Auto" color value.
       * Default value is the empty string "" in which no auto color is specified.
       * @instance
       */
        autoColor: '',

        /**
       * @property {boolean} showAuto - toggles showing the "Auto" color style.
       * @instance
       */
        showAuto: false,

        /**
       * @property {boolean} showNone - toggles showing the "None" color style.
       * @instance
       */
        showNone: false,

        /**
       * @property {boolean} showFlat - toggles showing the "Flat" color style.
       * @instance
       */
        showFlat: false,

        /**
       * @property {boolean} showInterp - toggles showing the "Interp" color style.
       * @instance
       */
        showInterp: false,

        /**
       * @property {boolean} showTexturemap - toggles showing the "Texturemap" color style.
       * @instance
       */
        showTexturemap: false,

        /**
       * @property {string} recentColorsTitle - Title of the Recent Colors palette.
       * @instance
       */
        recentColorsTitle: l10nStrs.recentColors,

        _colorStylesShown: 0,
        _STANDARD_COLORS: 'standard_colors',
        _RECENT_COLORS: 'recent_colors',
        _GRADIENT: 'gradient',
        _SPINNER: 'spinner',

        _displayViewType: {
            STANDARD: 'standard',
            CUSTOM: 'custom'
        },

        _isStarted: false,

        _parsedStandardColors: '',

        _previewTitle: l10nStrs.preview,

        templateString: template,

        baseClass: 'mwColorPicker',

        constructor: function () {
            this._set('recentlyUsedColors', ['#ff0000', '#ff00ff', '#ffff00', '#00ff00',
                '#ffffff', '#00ffff', '#0000ff'
            ]);
            this._set('standardColors', MColor.getStandardColors());
        },

        postMixInProperties: function () {
            this._set('_parsedStandardColors', JSON.stringify(this.get('standardColors')));
            this.inherited(arguments);
        },

        postCreate: function () {
            this.inherited(arguments);

            // Create toggle buttons
            // buttonGroup needs to be unique per mw-colorpicker widget instance
            this.standardButton = new ToggleButton({
                buttonGroup: 'ColorPickerViewButtons' + this.domNode.id,
                checked: true,
                icon: 'mwColorPickerToggleButtonStandard',
                width: 24,
                height: 24
            });
            this.standardButton.domNode.setAttribute('data-test-id', 'standardColorsButton');
            this.standardButton.placeAt(this.standardButtonNode);

            this.customButton = new ToggleButton({
                buttonGroup: 'ColorPickerViewButtons' + this.domNode.id,
                icon: 'mwColorPickerToggleButtonCustom',
                width: 24,
                height: 24
            });
            this.customButton.domNode.setAttribute('data-test-id', 'customColorsButton');
            this.customButton.placeAt(this.customButtonNode);

            this.paletteNode.set('showTitle', false);
            this.titleNode.textContent = l10nStrs.standardColors;
            this.previewContainerNode.textContent = l10nStrs.preview;
            this.standardButtonNode.setAttribute('title', l10nStrs.standardColors);
            this.customButtonNode.setAttribute('title', l10nStrs.customColors);
            this.own(
                this.paletteNode.on('change', this._handleWidgetChange.bind(this, this._STANDARD_COLORS)),
                this.paletteNode.on('previewchange', this._handlePaletteCellPreviewChange.bind(this)),
                on(this.standardButton, 'change', this._switchToStandard.bind(this)),
                on(this.customButton, 'change', this._switchToCustom.bind(this))
            );
        },

        _emitPreviewEvent: function (color) {
            this.emit('preview', {
                mwEventData: {
                    propertyName: 'value',
                    oldValue: this.get('value'),
                    newValue: color
                }
            });
        },

        _handleColorStylePreviewChange: function (e) {
            var color;
            if (e.eventData.type === 'mouseenter') {
                var previewedCell = this.standardPickerRow.querySelector('.dijitPaletteCell[tabindex="0"]');
                if (previewedCell) {
                    // remove affordance of any previewed cell selected through keyboard navigation
                    previewedCell.tabIndex = -1;
                }
                e.eventData.target.classList.add('mwPaletteCellMouseHover');
                this._setPreviewColorStyle(e.value);
                color = e.value;
            } else {
                e.eventData.target.classList.remove('mwPaletteCellMouseHover');
                color = this.get('value');
                this._setPreview(color);
            }
            this._emitPreviewEvent(color);
        },

        _setPreview: function (color) {
            if (this._isColorStyle(color)) {
                this._setPreviewColorStyle(color);
            } else {
                this._setPreviewColor(color);
            }
        },

        _handlePaletteCellPreviewChange: function (e) {
            var color = e.value; var previewedCell;
            if (e.eventData.type === 'mouseenter') {
                // when mouse pointer enters a standard palette cell, preview that cell

                previewedCell = this.standardPickerRow.querySelector('.dijitPaletteCell[tabindex="0"]');
                if (previewedCell) {
                    // remove affordance of any previewed cell selected through keyboard navigation
                    previewedCell.tabIndex = -1;
                }
                e.eventData.target.classList.add('mwPaletteCellMouseHover');
            } else if (e.eventData.type === 'focus') {
                // when a standard palette cell is in focus as a result of keyboard Navigation, preview that cell

                previewedCell = this.standardPickerRow.querySelector('.mwPaletteCellMouseHover');
                if (previewedCell) {
                    // remove affordance of any previewed cell resulting from mouseenter
                    previewedCell.classList.remove('mwPaletteCellMouseHover');
                }
            } else {
                // remove any previews and show the selected cell value when mouse pointer leaves a standard palette cell
                // or when cell is not in focus

                e.eventData.target.classList.remove('mwPaletteCellMouseHover');
                color = this.get('value');
            }
            this._setPreview(color);
            this._emitPreviewEvent(color);
        },

        startup: function () {
            this.inherited(arguments);
            this._isStarted = true;
            this.standardButton.startup();
            this.customButton.startup();

            // Needed to calculate appropriate sizing of spinners & update markers
            if (this.displayView === this._displayViewType.CUSTOM) {
                this.colorGradientNode.startup();
                this.colorSpinnerNode.startup();
            }
        },

        _setValueAttr: function (newColor) {
            if (this._isColorStyle(newColor)) {
                this._updateViewFromColorStyleChange(newColor);
            } else {
                if (this.displayView === this._displayViewType.STANDARD) {
                    // Update standard colors, recent colors, and color style values
                    this.paletteNode.set('value', newColor, false);
                    if (this._colorStylesShown) {
                        this._updateColorStyleSelection(this.value, newColor);
                    }
                    if (this.recentColorsWidgetNode) {
                        this.recentColorsWidgetNode.set('value', newColor, false);
                    }
                } else {
                    // Update gradient and spinner values
                    if (this.colorGradientNode !== undefined) {
                        this.colorGradientNode.set('value', newColor);
                        this.colorSpinnerNode.set('value', newColor);
                    }
                }

                this._setPreviewColor(newColor);
            }

            this._set('value', newColor);
        },

        _isColorStyle: function (newColor) {
            if ([ColorStyleEnum.AUTO,
                ColorStyleEnum.NONE,
                ColorStyleEnum.FLAT,
                ColorStyleEnum.INTERP,
                ColorStyleEnum.TEXTUREMAP].indexOf(newColor) === -1) {
                return false;
            } else {
                return true;
            }
        },

        _removeRecentColorsWidget: function () {
            if (this.recentColorsWidgetNode) {
                this.recentColorsWidgetNode.destroy();
                while (this.recentColorsNode.lastChild) {
                    this.recentColorsNode.removeChild(this.recentColorsNode.lastChild);
                }
            }
            this.recentColorsWidgetNode = null;
        },

        _createRecentColorsWidget: function () {
            this.recentColorsWidgetNode = new ColorPalette({
                palette: [this.recentlyUsedColors],
                title: this.get('recentColorsTitle'),
                value: this.value
            });
            this.own(
                this.recentColorsWidgetNode.on('change', this._handleWidgetChange.bind(this, this._RECENT_COLORS)),
                this.recentColorsWidgetNode.on('previewchange', this._handlePaletteCellPreviewChange.bind(this))
            );
            this.recentColorsWidgetNode.placeAt(this.recentColorsNode).startup();
        },

        _isEmptyRecentlyUsedColors: function () {
            return (this.recentlyUsedColors.length === 0);
        },

        _updateRecentColorsWidget: function () {
            this._removeRecentColorsWidget();
            if (this._isEmptyRecentlyUsedColors()) {
                return;
            }
            this._createRecentColorsWidget();
        },

        _setRecentlyUsedColorsAttr: function (newVal) {
            this._set('recentlyUsedColors', newVal.slice(0, 7));
            this._updateRecentColorsWidget();
        },

        _setRecentColorsTitleAttr: function (newVal) {
            this._set('recentColorsTitle', newVal);
            if (this.recentColorsWidgetNode) {
                this.recentColorsWidgetNode.set('title', newVal);
            }
        },

        _setDisplayViewAttr: function (newVal) {
            if (newVal === this._displayViewType.STANDARD) {
                this._switchToStandard();
                if (this.standardButton) {
                    this.standardButton.set('checked', true);
                }
            } else if (newVal === this._displayViewType.CUSTOM) {
                this._switchToCustom();
                if (this.customButton) {
                    this.customButton.set('checked', true);
                }
            }
        },

        /* Show color style setter methods */

        _setAutoColorAttr: function (newVal) {
            this._set('autoColor', newVal);
            this._updateAutoColorSwatch(newVal);

            // Update the view if the current value is equal to auto or the new auto color
            if (this.value === ColorStyleEnum.AUTO || this.value === newVal) {
                this._updateViewFromColorStyleChange(ColorStyleEnum.AUTO);
            }
        },

        _setShowAutoAttr: function (newVal) {
            this._handleColorStyleSet(newVal, 'autoColorNode', ColorStyleEnum.AUTO);
            this._updateAutoColorSwatch(this.autoColor);
        },

        _updateAutoColorSwatch: function (newVal) {
            if (this.autoColorNode) {
                if (newVal === '') {
                    this.autoColorNode.colorStyleSwatch.classList.add('mwColorStyleSwatchDefault');
                    this.autoColorNode.colorStyleSwatch.style.background = '';
                } else {
                    this.autoColorNode.colorStyleSwatch.classList.remove('mwColorStyleSwatchDefault');
                    var mcolor = new MColor(newVal);
                    this.autoColorNode.colorStyleSwatch.style.background = mcolor.toCss();
                }
            }
        },

        _setShowNoneAttr: function (newVal) {
            this._handleColorStyleSet(newVal, 'noneColorNode', ColorStyleEnum.NONE);
        },

        _setShowFlatAttr: function (newVal) {
            this._handleColorStyleSet(newVal, 'flatColorNode', ColorStyleEnum.FLAT);
        },

        _setShowInterpAttr: function (newVal) {
            this._handleColorStyleSet(newVal, 'interpColorNode', ColorStyleEnum.INTERP);
        },

        _setShowTexturemapAttr: function (newVal) {
            this._handleColorStyleSet(newVal, 'texturemapColorNode', ColorStyleEnum.TEXTUREMAP);
        },

        /* Color style  helper methods */

        _handleColorStyleSet: function (newVal, node, style) {
            var colorStyle = style.charAt(0).toUpperCase() + style.slice(1);
            if (newVal && this[node] === undefined) {
                this[node] = this._createColorStyle(style);
                // Show this color style as selected if the value is set to this color style already
                if (this.value === style) {
                    this._updateSelection(style, true);
                }
            } else if (!newVal) {
                this._destroyColorStyle(this[node]);
                this[node] = undefined;
            }
            this._set('show' + colorStyle, newVal);
        },

        _createColorStyle: function (value) {
            var newColorStyle = new ColorStyle({ value: value });
            newColorStyle.placeAt(this.stylesContainerNode);
            newColorStyle.startup();
            this.own(
                newColorStyle.on('change', this._handleColorStyleChange.bind(this)),
                newColorStyle.on('previewchange', this._handleColorStylePreviewChange.bind(this))
            );

            this._colorStylesShown++;
            return newColorStyle;
        },

        _destroyColorStyle: function (node) {
            if (node) {
                node.destroy();
                this._colorStylesShown--;
            }
        },

        /* Toggle button callbacks */

        _switchToCustom: function (evt) {
            if (evt) {
                evt.stopPropagation();
            }

            /* Title bar and toggle button updates */
            this.standardPickerRow.classList.add('mwColorPickerViewHidden');
            this.customPickerRow.classList.remove('mwColorPickerViewHidden');

            this.titleNode.textContent = l10nStrs.customColors;
            this.titleNode.classList.add('mwColorPickerTitleCustomColors');

            /* If this is the first time viewing "Custom Colors", then instantiate
              the ColorGradient and ColorSpinner widgets */
            if (!this.colorGradientNode) {
                this.colorGradientNode = new ColorGradient();
                this.own(this.colorGradientNode.on('change', this._handleGradientChange.bind(this)));
                this.own(this.colorGradientNode.on('preview', this._handleGradientPreview.bind(this)));
                this.colorGradientNode.placeAt(this.gradientContainerNode);

                this.colorSpinnerNode = new ColorSpinner();
                this.own(this.colorSpinnerNode.on('change', this._handleSpinnerChange.bind(this)));
                this.colorSpinnerNode.placeAt(this.spinnerContainerNode);

                /* Startup() is used to calculate appropriate sizing of spinners & update markers
                     This should not be called if mw-colorpicker widget hasn't been started yet */
                if (this._isStarted) {
                    this.colorGradientNode.startup();
                    this.colorSpinnerNode.startup();
                }
            }

            /* Adjust the height of the gradient depending on how many color styles are shown */
            /* Each row accomodates 2 color styles and requires 30px height for layout */
            /* When no color styles are shown height is 90px */
            var multiplier = Math.floor(this._colorStylesShown / 2) + this._colorStylesShown % 2;
            var height = 90 + multiplier * 30;
            this.colorGradientNode.domNode.style.setProperty('--color-gradient-height', height + 'px');
            if (this._colorStylesShown > 0) {
                this.colorGradientNode.domNode.classList.add('mwColorGradientWithStyles');
            } else {
                this.colorGradientNode.domNode.classList.remove('mwColorGradientWithStyles');
            }

            /* Update gradient and spinner values */
            this.colorGradientNode.set('value', this.value);
            this.colorSpinnerNode.set('value', this.value);
            this._set('displayView', this._displayViewType.CUSTOM);
        },

        _switchToStandard: function (evt) {
            if (evt) {
                evt.stopPropagation();
            }

            /* Title bar and toggle button updates */
            this.standardPickerRow.classList.remove('mwColorPickerViewHidden');
            this.customPickerRow.classList.add('mwColorPickerViewHidden');

            this.titleNode.textContent = l10nStrs.standardColors;
            this.titleNode.classList.remove('mwColorPickerTitleCustomColors');

            /* Update standard colors and recent colors  values */
            this.paletteNode.set('value', this.value, false);
            if (this.recentColorsWidgetNode) {
                this.recentColorsWidgetNode.set('value', this.value, false);
            }
            this._set('displayView', this._displayViewType.STANDARD);
        },

        /* On change callbacks */

        _handleGradientChange: function (evt) {
            var newColor = evt.mwEventData.newValue;
            evt.stopPropagation();
            this._handleWidgetChange(this._GRADIENT, newColor);
        },

        _handleSpinnerChange: function (evt) {
            var newColor = evt.mwEventData.newValue;
            evt.stopPropagation();
            this._handleWidgetChange(this._SPINNER, newColor);
        },

        _handleColorStyleChange: function (evt) {
            evt.stopPropagation();
            this._updateViewFromColorStyleChange(evt.value);
            this._setValueAndEmitChange(evt.value);
        },

        /* Color gradient preview event only updates the custom colors view and does not commit any value */
        _handleGradientPreview: function (evt) {
            var newColor = evt.mwEventData.newValue;
            this.colorSpinnerNode.set('value', newColor);
            this._setPreviewColor(newColor);
        },

        /* Updates the view of all components in the "Standard Colors" view based on a color style selection */
        _updateViewFromColorStyleChange: function (newValue) {
            var newPaletteValue = (newValue === ColorStyleEnum.AUTO) ? this.autoColor : null;
            if (this.recentColorsWidgetNode) {
                this.recentColorsWidgetNode.set('value', newPaletteValue, false);
            }
            this.paletteNode.set('value', newPaletteValue, false);

            this._updateColorStyleSelection(this.value, newValue);
            this._setPreviewColorStyle(newValue);
        },

        /*
       * Based on the component that updates, this method will update all other widgets
       * that are in the same view as the updated component.
       * Calling this method will emit a change event.
       */
        _handleWidgetChange: function (originOfChange, newColor) {
            switch (originOfChange) {
                case this._STANDARD_COLORS:
                    if (this.recentColorsWidgetNode) {
                        this.recentColorsWidgetNode.set('value', newColor, false);
                    }
                    break;
                case this._RECENT_COLORS:
                    this.paletteNode.set('value', newColor, false);
                    break;
                case this._GRADIENT:
                    this.colorSpinnerNode.set('value', newColor);
                    break;
                case this._SPINNER:
                    this.colorGradientNode.set('value', newColor);
            }
            // Clear the ColorStyle selection
            if (this._colorStylesShown) {
                this._updateSelection(this.value, false);
            }
            this._setPreviewColor(newColor);
            this._setValueAndEmitChange(newColor);
        },

        /* Other helper methods */

        _setValueAndEmitChange: function (newValue) {
            var oldValue = this.get('value');
            this._set('value', newValue);
            this.emit('change', {
                mwEventData: {
                    propertyName: 'value',
                    oldValue: oldValue,
                    newValue: newValue
                }
            });
        },

        _setPreviewColor: function (newColor) {
            var mcolor = new MColor(newColor);
            this.previewColorNode.style.background = mcolor.toCss();
            this.previewColorNode.textContent = '';
            this.previewColorNode.classList.remove('mwColorPickerPreviewNone');
            this.previewColorNode.classList.remove('mwColorPickerPreviewColorStyle');
        },

        _setPreviewColorStyle: function (newValue) {
            switch (newValue) {
                case ColorStyleEnum.AUTO:
                    if (this.autoColor) {
                        this._setPreviewColor(this.autoColor);
                    } else {
                        this._updatePreviewStyle(newValue);
                    }
                    break;
                case ColorStyleEnum.NONE:
                    this.previewColorNode.classList.add('mwColorPickerPreviewNone');
                    this.previewColorNode.classList.remove('mwColorPickerPreviewColorStyle');
                    this.previewColorNode.style.background = '';
                    this.previewColorNode.textContent = '';
                    break;
                case ColorStyleEnum.FLAT:
                case ColorStyleEnum.INTERP:
                case ColorStyleEnum.TEXTUREMAP:
                    this._updatePreviewStyle(newValue);
            }
        },

        _updatePreviewStyle: function (colorStyle) {
            this.previewColorNode.style.background = '';
            this.previewColorNode.classList.add('mwColorPickerPreviewColorStyle');
            this.previewColorNode.classList.remove('mwColorPickerPreviewNone');
            this.previewColorNode.textContent = l10nStrs[colorStyle];
        },

        _updateColorStyleSelection: function (oldValue, newValue) {
        // Deselect the previous color style
            this._updateSelection(oldValue, false);

            // Select the new color style
            this._updateSelection(newValue, true);
        },

        _updateSelection: function (colorStyle, selected) {
            switch (colorStyle) {
                case this.autoColor:
                    if (this.autoColorNode) {
                        this.autoColorNode.set('selected', selected);
                    }
                    break;
                case ColorStyleEnum.AUTO:
                case ColorStyleEnum.NONE:
                case ColorStyleEnum.FLAT:
                case ColorStyleEnum.INTERP:
                case ColorStyleEnum.TEXTUREMAP:
                    if (this[colorStyle + 'ColorNode']) {
                        this[colorStyle + 'ColorNode'].set('selected', selected);
                    }
                    break;
            }
        },

        /*
       * Testing hook for qeToolstripTools
       * Simulates a user interaction with the color picker by setting the value,
       * updating the current view, and firing a change event.
       * The input parameter is a string from the MATLAB ColorSpec
       */
        _qeSetColorByName: function (name) {
            var newHexValue = MColor.fromMATLABFriendlyName(name);
            this._qeSetColorAndEmitChange(newHexValue);
        },

        /*
       * Testing hook for qeToolstripTools
       * Simulates a user interaction with the color picker by setting the value,
       * updating the current view, and firing a change event.
       * The input parameter is a 1x3 array of R, G, B values represented as doubles on the [0 - 1] scale.
       */
        _qeSetColorByRGB: function (rgbArray) {
            var newColor = MColor.fromDoubleRGB(rgbArray);
            var newHexValue = newColor.toHex();
            this._qeSetColorAndEmitChange(newHexValue);
        },

        _qeSetColorAndEmitChange: function (newValue) {
            var oldValue = this.get('value');
            this.set('value', newValue);
            this.emit('change', {
                mwEventData: {
                    propertyName: 'value',
                    oldValue: oldValue,
                    newValue: newValue
                }
            });
        }
    });
});
